import { AppId, FCX, ID, ProductLogo, Tooltip, usePlatformContext, useUuid } from "@datassential/platform-ui-lib";
import cn from "classnames";
import { AiOutlineInfoCircle } from "react-icons/ai";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { requestTrialByFeature } from "@actions/page";
import Widget from "@components/Widget/Widget";
import { sanitizeUrl } from "@helpers";
import { PermissionLevelEnum } from "@models/PermissionSection";
import { FoodSectionEnum } from "@pages/Food/Food.constants";
import PermissionOverlay from "@pages/Food/components/PermissionOverlay/PermissionOverlay";
import FoodTabs from "@pages/Food/components/Tabs/Tabs";
import FoodSocialAssociations
    from "@pages/Food/components/SectionSocial/components/SocialAssociations/SocialAssociations";
import FoodSocialCustomAssociation
    from "@pages/Food/components/SectionSocial/components/SocialCustomAssociation/SocialCustomAssociation";
import FoodSocialMentions from "@pages/Food/components/SectionSocial/components/SocialMentions/SocialMentions";
import { FoodSocialMetricEnum } from "@models/FoodSocial";
import { FoodSocialSortFilterEnum } from "@pages/Food/components/SectionSocial/hooks/useFoodSocialSection.constants";
import useFoodSocialSection from "@pages/Food/components/SectionSocial/hooks/useFoodSocialSection";
import FoodSectionMoreLink from "@pages/Food/refresh/components/FoodSectionMoreLink/FoodSectionMoreLink";
import FoodSectionTitle from "@pages/Food/refresh/components/FoodSectionTitle/FoodSectionTitle";
import FoodSocialExamplePosts from "@pages/Food/refresh/components/FoodSocialExamplePosts/FoodSocialExamplePosts";
import "./FoodSocialSection.scss";

interface Props {
    word: string;
    wordId: ID;
    setHasData: (hasData: boolean) => void;
}

const METRIC_TABS = [
    { id: FoodSocialMetricEnum.Mentions, title: FoodSocialMetricEnum.Mentions },
    { id: FoodSocialMetricEnum.Engagement, title: FoodSocialMetricEnum.Engagement },
];

/**
 * Social section of the refreshed Food Profile. Same data and behaviour as the legacy section
 * (shared `useFoodSocialSection`), re-arranged: charts and the custom association search on the left,
 * the example posts on the right with a removable tag for the active filter.
 */
const FoodSocialSection: FCX<Props> = ({ word, wordId, setHasData }) => {
    const dispatch = useDispatch();
    const { userInfo: { products } } = usePlatformContext();
    const detailsTooltipId = useUuid();
    const metricTooltipId = useUuid();
    const {
        isLoading,
        hasData,
        permissionLevel,
        data,
        isCustomAssociationEnabled,
        selectedMetric,
        setSelectedMetric,
        selectedSortFilterMetric,
        setSelectedSortFilterMetric,
        selectedMonth,
        selectedYear,
        selectedAssociation,
        selectedCustomAssociation,
        onMentionBarClick,
        onAssociationBarClick,
        onCustomAssociationClick,
        clearFilter,
        activeFilter,
        customAssociationResetKey,
        associationLabel,
        postsData,
        postsDateRangeString,
        sortedByString,
        copyPostsData,
        postsTabs,
        postsSectionKey,
        isPostsLoading,
    } = useFoodSocialSection({ wordId, setHasData });

    if (isLoading) {
        return (
            <div className="Food__section is-loading">
                <div className="spinner spinner-blink">
                    <ProductLogo appId={AppId.Snap} type="regular"/>
                </div>
            </div>
        );
    }

    if (!hasData) return null;

    const hasAccess = permissionLevel === PermissionLevelEnum.Full;
    const trendingNow = products[AppId.TrendingNow];
    // A missing product counts as no access, so the link is never an enabled control that goes nowhere.
    const hasTrendingNow = !!trendingNow && !trendingNow.isDisabled;

    const requestTrial = (featureName: string, hideNotification?: boolean) => {
        if (!hideNotification) {
            toast.info('Request has been sent');
        }
        return dispatch(requestTrialByFeature(featureName));
    };

    return (
        <div
            className="Food__section"
            id={FoodSectionEnum.Social}
            data-testid="blockFoodSectionSocial"
        >
            <div
                className={cn("FoodSocialSection", !hasAccess && "is-blocked")}
                data-testid="blockFoodSocial"
            >
                <FoodSectionTitle
                    className="FoodSocialSection__title"
                    title={`Social: ${word}`}
                    testId="textFoodSocialTitle"
                />
                <div className="FoodSocialSection__card">
                    {/* Without access the values are only blurred (CSS). `inert` also takes the controls under the
                        overlay out of tab order and the accessibility tree, so nothing can be clicked, typed or
                        searched through it. React 18 has no `inert` prop, hence the ref. */}
                    <div
                        className="FoodSocialSection__grid"
                        aria-hidden={hasAccess ? undefined : true}
                        ref={(element) => element?.toggleAttribute('inert', !hasAccess)}
                    >
                        <div className="FoodSocialSection__left">
                            <Widget
                                testId="blockFoodSocialTotalMentions"
                                className="FoodSocialSection__mentions"
                                headerContent={(<>
                                    <div
                                        className="FoodSocialSection__widget-title"
                                        data-testid="textFoodSocialTotalMentionsTitle"
                                    >
                                        TOTAL MENTIONS
                                        <span className="Widget__header-sub-title ml">past 24 months</span>
                                    </div>
                                    <FoodTabs
                                        data-tooltip-id={metricTooltipId}
                                        data={METRIC_TABS}
                                        activeTabId={selectedMetric}
                                        onItemClick={(id) => setSelectedMetric(id as FoodSocialMetricEnum)}
                                        testId="blockFoodSocialTotalMentionsTabs"
                                    />
                                </>)}
                            >
                                <div className="FoodSocialSection__mentions-content">
                                    <div className="FoodSocialSection__hint">Click any month to see its posts</div>
                                    <FoodSocialMentions
                                        selectedMetric={selectedMetric}
                                        socialData={data}
                                        selectedMonth={selectedMonth}
                                        selectedYear={selectedYear}
                                        onMentionBarClick={onMentionBarClick}
                                    />
                                </div>
                            </Widget>
                            <FoodSocialAssociations
                                className="FoodSocialSection__associations"
                                word={word}
                                wordId={wordId}
                                selectedAssociation={selectedAssociation}
                                foodAssociations={data.foodAssociations}
                                placesAssociations={data.placesAssociations}
                                onItemClick={onAssociationBarClick}
                                hint={<div className="FoodSocialSection__hint">Click any term to see its posts</div>}
                            />
                            {isCustomAssociationEnabled && (
                                <div className="FoodSocialSection__search" data-testid="blockFoodSocialSearchPosts">
                                    <div className="FoodSocialSection__search-title">
                                        SEARCH WITHIN [{word}] POSTS
                                        <AiOutlineInfoCircle
                                            className="FoodSocialSection__search-info"
                                            data-tooltip-id={detailsTooltipId}
                                            aria-label="Data details"
                                        />
                                    </div>
                                    <div className="FoodSocialSection__search-text">
                                        Search any food, beverage, flavor, descriptor, or chain. See the count,
                                        growth, and actual [{word}] posts that mention it.
                                    </div>
                                    <FoodSocialCustomAssociation
                                        // Remounted empty when its tag is removed above the posts.
                                        key={customAssociationResetKey}
                                        wordId={wordId}
                                        selectedQuery={selectedCustomAssociation?.query}
                                        onResultClick={onCustomAssociationClick}
                                        placeholder="Enter search term here"
                                    />
                                </div>
                            )}
                        </div>
                        <div className="FoodSocialSection__right">
                            <FoodSocialExamplePosts
                                word={word}
                                subtitle={<>{sortedByString}, {postsDateRangeString}</>}
                                association={associationLabel}
                                filterLabel={activeFilter?.label ?? null}
                                onRemoveFilter={clearFilter}
                                tabs={postsTabs}
                                activeTabId={selectedSortFilterMetric}
                                onTabClick={(id) => setSelectedSortFilterMetric(id as FoodSocialSortFilterEnum)}
                                posts={postsData}
                                copyData={copyPostsData}
                                isLoading={isPostsLoading}
                                postsKey={postsSectionKey}
                            />
                        </div>
                    </div>
                    {!hasAccess && (
                        <PermissionOverlay
                            onContactUsClick={() => requestTrial('social')}
                            testId="blockFoodSocialPermissionOverlay"
                        >
                            <div>Upgrade your subscription to unlock</div>
                            <div>insights about <b>{word}</b></div>
                        </PermissionOverlay>
                    )}
                </div>
                <FoodSectionMoreLink
                    url={trendingNow?.url ? sanitizeUrl(trendingNow.url) : null}
                    text="More Social Trends & MAC"
                    altText="Upgrade your subscription to explore social trends."
                    hasAccess={hasTrendingNow}
                    onContactUsClick={() => requestTrial('social', true)}
                    testId="linkFoodSocialMoreSocialTrends"
                />
            </div>
            <Tooltip id={detailsTooltipId} place="bottom-start">
                <div className="FoodSocialSection__tooltip">
                    <b>Data Details</b>
                    <ul>
                        <li>updated weekly</li>
                        <li>cleaned and vetted by Datassential trend experts</li>
                        <li>sources include: TikTok, Instagram, Pinterest, YouTube, X, Reddit</li>
                        <li>reflects US & Canadian social activity</li>
                    </ul>
                </div>
            </Tooltip>
            <Tooltip id={metricTooltipId} place="bottom">
                <div className="FoodSocialSection__tooltip">
                    <b>Mentions</b>
                    <div>count of total mentions of this term</div>
                    <br/>
                    <b>Engagement</b>
                    <div>sum of likes, comment, and shares</div>
                </div>
            </Tooltip>
        </div>
    );
};

export default FoodSocialSection;
